import DialogProvider from "@components/dialog_provider"
import {
  cancelHumanReplyLearningJobMutation,
  getHumanReplyLearningJobsQuery,
  getHumanReplyLearningStatusQuery,
  HumanReplyLearningJob
} from "@domain/scripts"
import { useCustomersQuery } from "@hooks/use_customers"
import useTranslate from "@hooks/use_translate"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { fmtUsd, getTryRate } from "@utils/currency"
import { showErrorToast } from "@utils/toast"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import { useEffect, useRef, useState } from "react"

dayjs.extend(duration)

const LAST_CUSTOMER_KEY = "scripts:human-reply-learning:last-customer-id"
const RUNNING_STATUSES = new Set(["queued", "running", "cancelling"])
const CANCELLABLE_STATUSES = new Set(["queued", "running", "cancelling"])

const STATUS_CONFIG: Record<
  string,
  { label: string; color: "success" | "error" | "warning" | "info" | "default" }
> = {
  queued: { label: "Sıraya alındı", color: "info" },
  running: { label: "Çalışıyor", color: "warning" },
  cancelling: { label: "İptal ediliyor…", color: "warning" },
  cancelled: { label: "İptal edildi", color: "default" },
  completed: { label: "Tamamlandı", color: "success" },
  failed: { label: "Hata oluştu", color: "error" }
}

type Props = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  initialCustomerId?: string
  initialJobId?: string
}

function fmt(value?: number | null): string {
  return (value ?? 0).toLocaleString("tr-TR")
}

function fmtDate(value?: string | null): string {
  return value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "—"
}

function calcDuration(start?: string | null, end?: string | null): string {
  if (!start) return "—"
  const from = dayjs(start)
  const to = end ? dayjs(end) : dayjs()
  const diff = dayjs.duration(to.diff(from))
  const h = Math.floor(diff.asHours())
  const m = diff.minutes()
  const s = diff.seconds()
  if (h > 0) return `${h}s ${m}d ${s}sn`
  if (m > 0) return `${m}d ${s}sn`
  return `${s}sn`
}

function StatCard({
  label,
  value,
  sub,
  accent
}: {
  label: string
  value: string | number
  sub?: string
  accent?: "success" | "error" | "warning" | "info"
}) {
  const accentColors = {
    success: "#2e7d32",
    error: "#c62828",
    warning: "#e65100",
    info: "#1565c0"
  }
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}
    >
      <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        lineHeight={1.2}
        sx={{ color: accent ? accentColors[accent] : "text.primary" }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ letterSpacing: 1, fontSize: "0.7rem" }}
    >
      {children}
    </Typography>
  )
}

export default function HumanReplyLearningStatusDialog({
  isOpen,
  setIsOpen,
  initialCustomerId,
  initialJobId
}: Props) {
  const { translate } = useTranslate()
  const [customerId, setCustomerId] = useState("")
  const [selectedJobId, setSelectedJobId] = useState("")
  const [tryRate, setTryRate] = useState(0)
  const fetchedRateRef = useRef(false)

  useEffect(() => {
    if (fetchedRateRef.current) return
    fetchedRateRef.current = true
    getTryRate().then(setTryRate)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    if (initialCustomerId) setCustomerId(initialCustomerId)
    if (initialJobId) setSelectedJobId(initialJobId)
    const stored = localStorage.getItem(LAST_CUSTOMER_KEY)
    if (!initialCustomerId && stored) setCustomerId(stored)
  }, [isOpen, initialCustomerId, initialJobId])

  const { customerOptions, isLoading: customersLoading } =
    useCustomersQuery(isOpen)

  const {
    data: latestJob,
    isFetching: latestLoading,
    refetch: refetchLatest
  } = useQuery({
    queryKey: ["hrl-status", customerId],
    enabled: isOpen && !!customerId,
    queryFn: async () => {
      const r = await getHumanReplyLearningStatusQuery(customerId)
      if (!r.isSuccess) {
        showErrorToast(r.errorMessage ?? "Durum yüklenemedi")
        return undefined
      }
      return r.data ?? null
    },
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s && RUNNING_STATUSES.has(s) ? 5000 : false
    }
  })

  const {
    data: recentJobs = [],
    isFetching: recentLoading,
    refetch: refetchRecent
  } = useQuery({
    queryKey: ["hrl-jobs", customerId],
    enabled: isOpen && !!customerId,
    queryFn: async () => {
      const r = await getHumanReplyLearningJobsQuery(customerId)
      if (!r.isSuccess) {
        showErrorToast(r.errorMessage ?? "İşler yüklenemedi")
        return []
      }
      return r.data ?? []
    },
    refetchInterval: (q) => {
      const s = latestJob?.status ?? q.state.data?.[0]?.status
      return s && RUNNING_STATUSES.has(s) ? 5000 : false
    }
  })

  useEffect(() => {
    if (selectedJobId) return
    if (
      initialJobId &&
      (!initialCustomerId || customerId === initialCustomerId)
    ) {
      setSelectedJobId(initialJobId)
      return
    }
    if (latestJob?.jobId) {
      setSelectedJobId(latestJob.jobId)
      return
    }
    if (recentJobs[0]?.jobId) setSelectedJobId(recentJobs[0].jobId)
  }, [
    selectedJobId,
    initialJobId,
    initialCustomerId,
    customerId,
    latestJob?.jobId,
    recentJobs
  ])

  const selectedJob: HumanReplyLearningJob | null =
    recentJobs.find((j) => j.jobId === selectedJobId) ||
    (latestJob?.jobId === selectedJobId ? latestJob : null) ||
    latestJob ||
    recentJobs[0] ||
    null

  const isFetching = latestLoading || recentLoading
  const isRunning = !!selectedJob && RUNNING_STATUSES.has(selectedJob.status)
  const canCancel =
    !!selectedJob && CANCELLABLE_STATUSES.has(selectedJob.status)

  function handleClose() {
    setSelectedJobId(initialJobId || "")
    setIsOpen(false)
  }

  function handleRefresh() {
    void refetchLatest()
    void refetchRecent()
  }

  async function handleCancel() {
    if (!selectedJob || !customerId) return
    if (!CANCELLABLE_STATUSES.has(selectedJob.status)) return
    if (!window.confirm(`${selectedJob.jobId} işi iptal edilsin mi?`)) return
    const r = await cancelHumanReplyLearningJobMutation(
      customerId,
      selectedJob.jobId
    )
    if (!r.isSuccess) {
      showErrorToast(r.errorMessage ?? "İptal edilemedi")
      return
    }
    handleRefresh()
  }

  const statusCfg = selectedJob
    ? (STATUS_CONFIG[selectedJob.status] ?? {
        label: selectedJob.status,
        color: "default" as const
      })
    : null

  const totalCost = selectedJob?.totalEstimatedCost ?? 0

  const elapsedLabel = selectedJob
    ? calcDuration(
        selectedJob.startedAt,
        isRunning ? null : selectedJob.finishedAt
      )
    : "—"

  return (
    <DialogProvider
      title="Human Reply Learning"
      leftButton={{
        text: canCancel ? "İşi İptal Et" : translate("close"),
        color: canCancel ? "error" : "secondary",
        onClick: canCancel ? handleCancel : handleClose,
        disabled: isFetching
      }}
      rightButton={{
        text: "Yenile",
        color: "primary",
        onClick: handleRefresh,
        loading: isFetching,
        disabled: isFetching
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="large"
    >
      <Stack gap={2.5}>
        {/* Customer selector */}
        <Autocomplete
          options={customerOptions}
          disabled={customersLoading || isFetching}
          value={customerOptions.find((o) => o.id === customerId) ?? null}
          onChange={(_, option) => {
            const v = option?.id ?? ""
            setCustomerId(v)
            setSelectedJobId("")
            if (v) localStorage.setItem(LAST_CUSTOMER_KEY, v)
          }}
          getOptionLabel={(o) => `${o.label} (${o.id})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate("customer")}
              placeholder="Müşteri adı veya ID ile arayın…"
            />
          )}
          noOptionsText="Müşteri bulunamadı"
          loadingText="Yükleniyor…"
          loading={customersLoading}
        />

        {/* Loading */}
        {isFetching && !selectedJob && (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {/* No job */}
        {!isFetching && !selectedJob && customerId && (
          <Alert severity="info">
            Bu müşteri için henüz bir Human Reply Learning işi başlatılmamış.
          </Alert>
        )}

        {selectedJob && (
          <Stack gap={3}>
            {/* ── STATUS ── */}
            <Stack gap={1}>
              <SectionTitle>Durum</SectionTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap"
                }}
              >
                <Chip
                  label={statusCfg?.label ?? selectedJob.status}
                  color={statusCfg?.color ?? "default"}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
                {isRunning && <CircularProgress size={14} thickness={5} />}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {selectedJob.jobId}
                </Typography>
                <Chip
                  label={
                    selectedJob.mode === "INITIAL"
                      ? "İlk Kurulum"
                      : "Konuşma Bazlı"
                  }
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Stack>

            {/* ── PROGRESS ── */}
            <Stack gap={1}>
              <SectionTitle>İlerleme</SectionTitle>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {fmt(selectedJob.processedConversations)} /{" "}
                    {fmt(selectedJob.totalConversations)} konuşma
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    %{selectedJob.progressPercent.toFixed(1)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(selectedJob.progressPercent, 100)}
                  sx={{ height: 8, borderRadius: 4 }}
                  color={
                    selectedJob.status === "failed"
                      ? "error"
                      : selectedJob.status === "completed"
                        ? "success"
                        : "primary"
                  }
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 1,
                  mt: 0.5
                }}
              >
                <StatCard
                  label="İşlendi"
                  value={fmt(selectedJob.processedConversations)}
                  accent="success"
                />
                <StatCard
                  label="Atlandı"
                  value={fmt(selectedJob.skippedConversations)}
                />
                <StatCard
                  label="Hatalı"
                  value={fmt(selectedJob.failedConversations)}
                  accent={
                    selectedJob.failedConversations > 0 ? "error" : undefined
                  }
                />
                <StatCard
                  label="Kalan"
                  value={fmt(selectedJob.remainingConversations)}
                  accent={isRunning ? "info" : undefined}
                />
              </Box>
            </Stack>

            {/* ── LEARNING RESULTS ── */}
            <Stack gap={1}>
              <SectionTitle>Öğrenme Sonuçları</SectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1
                }}
              >
                <StatCard
                  label="Çıkarılan Soru-Cevap"
                  value={fmt(selectedJob.extractedPairs)}
                  sub="Ham çıktı"
                />
                <StatCard
                  label="Kaydedilen Soru-Cevap"
                  value={fmt(selectedJob.savedPairs)}
                  sub="Eğitime eklendi"
                  accent="success"
                />
                <StatCard
                  label="Tekrar Atlandı"
                  value={fmt(selectedJob.duplicatePairs)}
                  sub="Zaten mevcut"
                />
              </Box>
            </Stack>

            {/* ── COST & TOKENS ── */}
            <Stack gap={1}>
              <SectionTitle>Maliyet & Token Kullanımı</SectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 1
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, gridColumn: { sm: "span 1" } }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Toplam Maliyet
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25 }}>
                    {fmtUsd(totalCost, 6)}
                  </Typography>
                  {tryRate > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      ≈ ₺{(totalCost * tryRate).toFixed(4)}
                    </Typography>
                  )}
                </Paper>
                <StatCard
                  label="Girdi Token"
                  value={fmt(selectedJob.totalInputTokens)}
                />
                <StatCard
                  label="Çıktı Token"
                  value={fmt(selectedJob.totalOutputTokens)}
                />
              </Box>
            </Stack>

            {/* ── TIMELINE ── */}
            <Stack gap={1}>
              <SectionTitle>Zamanlama</SectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
                  gap: 1
                }}
              >
                <StatCard
                  label="Oluşturuldu"
                  value={fmtDate(selectedJob.createdAt)}
                />
                <StatCard
                  label="Başladı"
                  value={fmtDate(selectedJob.startedAt)}
                />
                <StatCard
                  label="Bitti"
                  value={fmtDate(selectedJob.finishedAt)}
                />
                <StatCard
                  label={isRunning ? "Geçen Süre" : "Toplam Süre"}
                  value={elapsedLabel}
                />
              </Box>
            </Stack>

            {/* ── MODEL USAGE ── */}
            {selectedJob.usageByModel.length > 0 && (
              <Accordion
                disableGutters
                variant="outlined"
                sx={{ borderRadius: "8px !important" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={600}>
                    Model Kullanımı ({selectedJob.usageByModel.length} model)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={1}>
                    {selectedJob.usageByModel.map((item) => {
                      const pct =
                        totalCost > 0
                          ? ((item.estimatedCost / totalCost) * 100).toFixed(1)
                          : "0"
                      return (
                        <Box
                          key={item.model}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "2fr 1fr 1fr 1fr"
                            },
                            gap: 1,
                            alignItems: "center",
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.model}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {fmt(item.requests)} istek
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Girdi
                            </Typography>
                            <Typography variant="body2">
                              {fmt(item.inputTokens)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Çıktı
                            </Typography>
                            <Typography variant="body2">
                              {fmt(item.outputTokens)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Maliyet
                            </Typography>
                            <Tooltip
                              title={
                                tryRate > 0
                                  ? `₺${(item.estimatedCost * tryRate).toFixed(4)}`
                                  : ""
                              }
                              arrow
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {fmtUsd(item.estimatedCost, 6)}{" "}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {pct}%
                                </Typography>
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>
                      )
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* ── COST BREAKDOWN ── */}
            {selectedJob.costBreakdown.length > 0 && (
              <Accordion
                disableGutters
                variant="outlined"
                sx={{ borderRadius: "8px !important" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={600}>
                    Aşamaya Göre Maliyet ({selectedJob.costBreakdown.length}{" "}
                    aşama)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={1}>
                    {selectedJob.costBreakdown.map((item) => {
                      const pct =
                        totalCost > 0
                          ? ((item.estimatedCost / totalCost) * 100).toFixed(1)
                          : "0"
                      return (
                        <Box
                          key={`${item.stage}-${item.model}`}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "2fr 1fr 1fr 1fr"
                            },
                            gap: 1,
                            alignItems: "center",
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.stage}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.model} • {fmt(item.requests)} istek
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Girdi
                            </Typography>
                            <Typography variant="body2">
                              {fmt(item.inputTokens)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Çıktı
                            </Typography>
                            <Typography variant="body2">
                              {fmt(item.outputTokens)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Maliyet
                            </Typography>
                            <Tooltip
                              title={
                                tryRate > 0
                                  ? `₺${(item.estimatedCost * tryRate).toFixed(4)}`
                                  : ""
                              }
                              arrow
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {fmtUsd(item.estimatedCost, 6)}{" "}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {pct}%
                                </Typography>
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>
                      )
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* ── RECENT JOBS ── */}
            {recentJobs.length > 1 && (
              <Accordion
                disableGutters
                variant="outlined"
                sx={{ borderRadius: "8px !important" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={600}>
                    Geçmiş İşler ({recentJobs.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={0.75}>
                    {recentJobs.map((job) => {
                      const cfg = STATUS_CONFIG[job.status] ?? {
                        label: job.status,
                        color: "default" as const
                      }
                      const isSelected = job.jobId === selectedJobId
                      return (
                        <Box
                          key={job.jobId}
                          onClick={() => setSelectedJobId(job.jobId)}
                          sx={{
                            p: 1.5,
                            border: "1px solid",
                            borderColor: isSelected
                              ? "primary.main"
                              : "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: isSelected
                              ? "action.selected"
                              : "background.paper",
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                            "&:hover": { bgcolor: "action.hover" }
                          }}
                        >
                          <Chip
                            label={cfg.label}
                            color={cfg.color}
                            size="small"
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {job.jobId}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {fmtDate(job.createdAt)} •{" "}
                              {fmt(job.processedConversations)}/
                              {fmt(job.totalConversations)} işlendi •{" "}
                              {fmt(job.savedPairs)} kaydedildi
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* ── FAILURES ── */}
            {selectedJob.failures?.length > 0 && (
              <Accordion
                disableGutters
                variant="outlined"
                sx={{ borderRadius: "8px !important" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="error.main"
                  >
                    Hatalar ({selectedJob.failures.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={0.75}>
                    {selectedJob.failures.slice(0, 20).map((f, i) => (
                      <Alert
                        severity="error"
                        key={`${f.conversationId}-${i}`}
                        sx={{ py: 0.5 }}
                      >
                        <Typography variant="caption">
                          <b>{f.conversationId || f.customerId || "—"}</b>:{" "}
                          {f.reason || "Bilinmeyen hata"}
                        </Typography>
                      </Alert>
                    ))}
                    {selectedJob.failures.length > 20 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        textAlign="center"
                      >
                        +{selectedJob.failures.length - 20} hata daha var
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        )}
      </Stack>
    </DialogProvider>
  )
}
